/**
 * Manual Journal modal (design "Modals") — editable debit/credit grid + live
 * balance bar (debits == credits AND total > 0). Post → createJournal service.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, Text, TextInput, View } from 'react-native';
import ModalShell from './modalShell';
import { useTheme } from '../../context/themeContext';
import SkIcon from '../skIcon';
import { fmtMoney } from '../../utils/format';
import { useBaseCurrency } from '../../context/currencyContext';
import { createJournal } from '../../services/billing/journal.service';
import { getTrialBalance } from '../../services/billing/accountant.service';

interface Account { id: string; label: string; }
interface Row { account: string; debit: string; credit: string; }

const ManualJournalModal: React.FC<{ visible: boolean; onClose: () => void }> = ({ visible, onClose }) => {
  const { theme } = useTheme();
  const { baseCurrency: cur } = useBaseCurrency();
  const { colors, font } = theme;
  const [narration, setNarration] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [rows, setRows] = useState<Row[]>([
    { account: '', debit: '', credit: '' },
    { account: '', debit: '', credit: '' },
  ]);

  useEffect(() => {
    if (!visible) return;
    let alive = true;
    getTrialBalance()
      .then((tb: any) => {
        if (!alive) return;
        const accts: Account[] = (Array.isArray(tb?.accounts) ? tb.accounts : []).map((a: any) => ({ id: a.id, label: `${a.code} · ${a.name}` }));
        setAccounts(accts);
        if (accts.length) setRows((rs) => rs.map((r, i) => (r.account ? r : { ...r, account: accts[Math.min(i, accts.length - 1)].label })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [visible]);

  const labels = accounts.map((a) => a.label);

  const { td, tc, balanced } = useMemo(() => {
    const td = rows.reduce((s, r) => s + (parseFloat(r.debit) || 0), 0);
    const tc = rows.reduce((s, r) => s + (parseFloat(r.credit) || 0), 0);
    return { td, tc, balanced: Math.abs(td - tc) < 0.005 && td > 0 };
  }, [rows]);
  const canPost = balanced && narration.trim().length > 0 && rows.every((r) => r.account);

  const cycle = (cur: string) => (labels.length ? labels[(labels.indexOf(cur) + 1) % labels.length] : cur);
  const setRow = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const post = async () => {
    if (!canPost) return;
    try {
      await createJournal({
        narration,
        lines: rows.map((r) => ({ account_id: accounts.find((a) => a.label === r.account)?.id || r.account, debit: r.debit || undefined, credit: r.credit || undefined })),
      });
      onClose();
    } catch {
      /* error toast shown by service */
    }
  };

  const inp = { borderWidth: 1, borderColor: colors.line, borderRadius: 5, height: 30, paddingHorizontal: 8, fontFamily: font.mono, fontSize: 12, color: colors.ink, textAlign: 'right' as const, ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}) };

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="New manual journal"
      subtitle="Append-only · post a reversal to correct"
      width={720}
      footer={
        <>
          <Pressable onPress={onClose} style={{ height: 34, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.line, borderRadius: 5, justifyContent: 'center' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable onPress={post} style={{ height: 34, paddingHorizontal: 16, borderRadius: 5, justifyContent: 'center', backgroundColor: canPost ? colors.ink : colors.paper3, borderWidth: 1, borderColor: canPost ? colors.ink : colors.line }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: canPost ? colors.paper : colors.ink4 }}>Post entry</Text>
          </Pressable>
        </>
      }
    >
      <View style={{ gap: 12 }}>
        <View>
          <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink2, marginBottom: 4, fontWeight: '500' }}>Narration (required)</Text>
          <TextInput value={narration} onChangeText={setNarration} placeholder="What is this entry for?" placeholderTextColor={colors.ink4} style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 5, height: 34, paddingHorizontal: 11, fontFamily: font.sans, fontSize: 13, color: colors.ink, ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}) }} />
        </View>
        <View style={{ flexDirection: 'row', paddingBottom: 4 }}>
          <Text style={{ flex: 1, fontFamily: font.sans, fontSize: 11, color: colors.ink3, textTransform: 'uppercase' }}>Account</Text>
          <Text style={{ width: 110, textAlign: 'right', fontFamily: font.sans, fontSize: 11, color: colors.ink3, textTransform: 'uppercase' }}>Debit</Text>
          <Text style={{ width: 110, textAlign: 'right', fontFamily: font.sans, fontSize: 11, color: colors.ink3, textTransform: 'uppercase' }}>Credit</Text>
          <View style={{ width: 28 }} />
        </View>
        {rows.map((r, i) => (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Pressable onPress={() => setRow(i, { account: cycle(r.account) })} style={{ flex: 1, height: 30, justifyContent: 'center', paddingHorizontal: 8, borderWidth: 1, borderColor: colors.line, borderRadius: 5 }}>
              <Text style={{ fontFamily: font.sans, fontSize: 12, color: r.account ? colors.ink2 : colors.ink4 }}>{r.account || 'Select account'}</Text>
            </Pressable>
            <TextInput value={r.debit} onChangeText={(t) => setRow(i, { debit: t, credit: '' })} keyboardType="decimal-pad" style={[inp, { width: 110 }]} />
            <TextInput value={r.credit} onChangeText={(t) => setRow(i, { credit: t, debit: '' })} keyboardType="decimal-pad" style={[inp, { width: 110 }]} />
            <Pressable onPress={() => setRows((rs) => rs.filter((_, idx) => idx !== i))} style={{ width: 28, alignItems: 'center' }}>
              <SkIcon name="x" size={14} color={colors.ink4} />
            </Pressable>
          </View>
        ))}
        <Pressable onPress={() => setRows((rs) => [...rs, { account: labels[0] || '', debit: '', credit: '' }])} style={{ alignSelf: 'flex-start' }}>
          <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.accent, fontWeight: '500' }}>+ Add row</Text>
        </Pressable>

        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 24, paddingTop: 4 }}>
          <Text style={{ fontFamily: font.mono, fontSize: 12, color: colors.ink3 }}>Dr {fmtMoney(td, cur)}</Text>
          <Text style={{ fontFamily: font.mono, fontSize: 12, color: colors.ink3 }}>Cr {fmtMoney(tc, cur)}</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 10, borderRadius: 5, backgroundColor: balanced ? colors.successSoft : colors.warnSoft, borderWidth: 1, borderColor: balanced ? colors.successLine : colors.warnLine }}>
          <SkIcon name={balanced ? 'check' : 'alert'} size={14} color={balanced ? colors.success : colors.warn} strokeWidth={2.4} />
          <Text style={{ fontFamily: font.sans, fontSize: 12, fontWeight: '500', color: balanced ? colors.success : colors.warn }}>
            {balanced ? 'Entry balances — ready to post' : `Debits and credits must be equal (${(td - tc >= 0 ? '+' : '−') + fmtMoney(Math.abs(td - tc), cur)})`}
          </Text>
        </View>
      </View>
    </ModalShell>
  );
};

export default ManualJournalModal;
